import React, { useState } from 'react';
import {
  Box, Container, Typography, Grid, Card, CardContent,
  IconButton, Divider, Chip, TextField, Button, Paper, Avatar, Tab, Tabs, CardMedia,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  LocationOn, Phone, Email, School, Facebook, Twitter,
  Instagram, YouTube, WhatsApp, DirectionsBus,
  Map as MapIcon, People, Star, EmojiEvents, Favorite,
  Send, ArrowForward, CheckCircle, Download, AccountBalance,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

const C = {
  primary:   '#1a237e',
  secondary: '#0d47a1',
  accent:    '#ff6f00',
  gold:      '#ffd700',
  dark:      '#0a0a2a',
  light:     '#f4f7fb',
  white:     '#ffffff',
  green:     '#2e7d32',
};

const GlassCard = styled(Card)(() => ({
  background: C.white,
  borderRadius: 20,
  border: '1px solid rgba(26, 35, 126, 0.08)',
  boxShadow: '0 8px 30px rgba(26, 35, 126, 0.08)',
  overflow: 'hidden',
}));

const AccentButton = styled(Button)(() => ({
  borderRadius: 8, padding: '12px 28px', textTransform: 'none', fontWeight: 700, fontSize: '0.95rem',
  background: `linear-gradient(135deg, ${C.accent}, #e65100)`, color: C.white,
  '&:hover': { background: `linear-gradient(135deg, #e65100, ${C.accent})`, transform: 'translateY(-2px)' },
  transition: 'all 0.3s ease',
}));

const fieldSx = {
  '& label.Mui-focused': { color: C.primary },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: C.primary },
  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: C.accent },
};

const SectionHeading = ({ children, subtitle }) => (
  <Box sx={{ textAlign: 'center', mb: 5 }}>
    <Typography variant="h3" sx={{ fontWeight: 800, color: C.primary, mb: 1, letterSpacing: '-0.5px', fontSize: { xs: '1.8rem', md: '2.5rem' } }}>
      {children}
    </Typography>
    <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
      <Box sx={{ width: 40, height: 4, borderRadius: 2, bgcolor: C.primary, mr: 1 }} />
      <Box sx={{ width: 12, height: 4, borderRadius: 2, bgcolor: C.accent }} />
      <Box sx={{ width: 40, height: 4, borderRadius: 2, bgcolor: C.primary, ml: 1 }} />
    </Box>
    {subtitle && <Typography variant="h6" sx={{ color: 'text.secondary', fontWeight: 400, fontSize: { xs: '0.95rem', md: '1.1rem' } }}>{subtitle}</Typography>}
  </Box>
);

const SCHOOLS = {
  secondary: {
    label:        'Kaozya High School',
    shortLabel:   'Secondary',
    regNo:        'S.4913',
    nectaNo:      'S.5430',
    tin:          '118-249-437',
    email:        'kaozyahighsch@gmail.com',
    address:      'Kaozya Road, Jengo No. 54, Mtaa wa Usalama, S.L.P 188, Kalengakelu, Mlimba, Morogoro',
    color:        C.primary,
    image:        '/images/kaozya-high-school-campus.jpg',
    poster:       '/images/kaozya-secondary-admission.jpg',
    downloadPdf:  '/downloads/FOMU_YA_KUJIUNGA_NA_KAOZYA_HIGH_SCHOOL.pdf',
    bankAccounts: [
      { bank: 'CRDB Bank', acc: '0150270576600', name: 'KAOZYA HIGH SCHOOL' },
      { bank: 'NMB Bank', acc: '24410021219', name: 'KAOZYA HIGH SCHOOL' },
    ],
    contacts: [
      { role: 'Mkuu wa Shule (Headmaster)',           numbers: ['+255 784 268 575', '+255 622 268 575'] },
      { role: 'Makamu Mkuu (Deputy Head)',             numbers: ['+255 658 268 575', '+255 735 268 575'] },
      { role: 'Meneja wa Shule (School Manager)',      numbers: ['+255 679 268 575', '+255 739 268 575'] },
      { role: 'Muhasibu (Accountant)',                 numbers: ['+255 772 268 575', '+255 767 268 575'] },
      { role: 'Mapokezi (Reception)',                  numbers: ['+255 773 268 575', '+255 736 268 575'] },
    ],
    stats: [
      { label: 'Students Enrolled',     value: '500+',  color: C.primary },
      { label: 'Qualified Teachers',    value: '25+',   color: C.secondary },
      { label: 'Form 5-6 Combinations', value: '10',    color: '#2e7d32' },
      { label: 'Years of Excellence',   value: '15+',   color: C.accent },
      { label: 'Installment Terms',     value: '4 Awamu', color: '#7b1fa2' },
    ],
    tags: ['Day & Boarding School', 'Kidato cha 1–6', 'Pre-Form 1 & 5', 'Science · Business · Arts', 'Mlimba, Morogoro'],
  },
  primary: {
    label:        'Kaozya English Medium School',
    shortLabel:   'English Medium',
    regNo:        'EM.21666',
    examCentre:   'PS11011193',
    email:        'kaozyaenglishmediumschool@gmail.com',
    address:      'Kaozya Road, Jengo No. 54, Mtaa wa Usalama, S.L.P 188, Kalengakelu, Mlimba, Morogoro',
    color:        '#2e7d32',
    image:        '/images/kaozya-primary-campus.jpg',
    poster:       '/images/kaozya-primary-admission.jpg',
    downloadPdf:  '/downloads/FOMU_YA_KUJIUNGA_NA_KAOZYA_ENGLISH_MEDIUM.pdf',
    bankAccounts: [
      { bank: 'CRDB Bank', acc: '10323210565', name: 'KAOZYA ENGLISH MEDIUM SCHOOL' },
    ],
    contacts: [
      { role: 'Mkuu wa Shule (Head Teacher)',          numbers: ['+255 787 705 910', '+255 655 705 910'] },
      { role: 'Makamu Mkuu (Deputy Head)',             numbers: ['+255 767 705 910', '+255 735 705 910'] },
      { role: 'Muhasibu (Accountant)',                 numbers: ['+255 772 705 910'] },
      { role: 'Mapokezi (Reception)',                  numbers: ['+255 736 705 910'] },
      { role: 'Meneja wa Shule (School Manager)',      numbers: ['+255 679 268 575', '+255 739 268 575'] },
    ],
    stats: [
      { label: 'Students Enrolled',    value: '300+', color: '#2e7d32' },
      { label: 'Qualified Teachers',   value: '15+',  color: '#1565c0' },
      { label: 'School Bus Transport', value: 'Included', color: C.accent },
      { label: 'Classes',              value: 'Baby to Std 6', color: '#6a1b9a' },
      { label: 'Curriculum',           value: 'British + NECTA', color: '#00695c' },
    ],
    tags: ['English Medium', 'Baby to Std 6', 'Free Bus Transport', 'Breakfast & Lunch', 'Mlimba, Morogoro'],
  },
};

const OWNER = {
  name:    'Henry Laurent Kaozya',
  title:   'Mmiliki wa Shule Zote (School Owner & Director)',
  numbers: ['+255 713 862 758', '+255 735 862 758', '+255 622 862 758', '+255 767 862 758'],
};

const values = [
  { icon: <Star />,        label: 'Excellence',  desc: 'We set high academic standards with modern laboratories and dedicated teachers.' },
  { icon: <Favorite />,   label: 'Integrity',   desc: 'Honesty, moral discipline, and high ethics guide all student activities.' },
  { icon: <People />,     label: 'Community',   desc: 'We provide safe, family-like boarding and caring day scholar transport.' },
  { icon: <EmojiEvents />,label: 'Achievement', desc: 'Consistent top performance in national NECTA exams and sports competitions.' },
];

const AboutPage = () => {
  const navigate = useNavigate();
  const [schoolTab, setSchoolTab] = useState(0);
  const [form, setForm]           = useState({ name: '', email: '', phone: '', message: '' });
  const [sent, setSent]           = useState(false);

  const currentSchool = schoolTab === 0 ? SCHOOLS.secondary : SCHOOLS.primary;

  const handleSend = () => {
    setSent(true);
    setTimeout(() => setSent(false), 4000);
    setForm({ name: '', email: '', phone: '', message: '' });
  };

  return (
    <Box sx={{ overflowX: 'hidden', bgcolor: C.light }}>

      {/* Hero */}
      <Box sx={{
        position: 'relative', minHeight: { xs: 260, md: 340 }, display: 'flex', alignItems: 'center',
        background: `linear-gradient(135deg, ${C.dark} 0%, ${C.primary} 60%, ${C.secondary} 100%)`,
        overflow: 'hidden',
      }}>
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2, pt: { xs: 6, md: 8 }, pb: 6 }}>
          <Chip label="Kaozya Schools — Mlimba, Morogoro"
            icon={<School sx={{ color: `${C.gold} !important` }} />}
            sx={{ mb: 2, bgcolor: `${C.gold}20`, color: C.gold, fontWeight: 700, border: `1px solid ${C.gold}40` }} />
          <Typography variant="h2" sx={{
            fontWeight: 900, color: C.white, fontSize: { xs: '2rem', md: '3.2rem' }, lineHeight: 1.2, mb: 1,
          }}>
            About Us &amp; Official Info
          </Typography>
          <Typography variant="h6" sx={{ color: C.gold, fontWeight: 400, fontSize: { xs: '1rem', md: '1.25rem' } }}>
            Quality Education For A Better Future — Kalengakelu, Mlimba, Morogoro, Tanzania
          </Typography>
        </Container>
      </Box>

      {/* School Tabs */}
      <Box sx={{ bgcolor: C.white, borderBottom: `2px solid ${C.primary}15`, position: 'sticky', top: 0, zIndex: 10 }}>
        <Container maxWidth="lg">
          <Tabs value={schoolTab} onChange={(_, v) => setSchoolTab(v)}
            sx={{ '& .MuiTab-root': { fontWeight: 800, textTransform: 'none', fontSize: 16, py: 2 },
              '& .Mui-selected': { color: C.primary }, '& .MuiTabs-indicator': { bgcolor: C.accent, height: 3 } }}>
            <Tab label="🏫 Kaozya High School (Form 1–6)" />
            <Tab label="📚 Kaozya English Medium (Pre & Primary)" />
          </Tabs>
        </Container>
      </Box>

      {/* About School Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
        <SectionHeading subtitle={`Reg: ${currentSchool.regNo}${currentSchool.nectaNo ? ` · NECTA: ${currentSchool.nectaNo}` : ''}${currentSchool.examCentre ? ` · Centre: ${currentSchool.examCentre}` : ''}`}>
          {currentSchool.label}
        </SectionHeading>

        <Grid container spacing={4} alignItems="stretch">
          {/* Information */}
          <Grid item xs={12} md={7}>
            <GlassCard sx={{ p: 4, height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Typography variant="body1" sx={{ fontSize: '1.05rem', lineHeight: 1.8, color: 'text.secondary', mb: 2 }}>
                {schoolTab === 0
                  ? 'Kaozya High School ni shule ya bweni na kutwa kwa wavulana na wasichana kuanzia kidato cha kwanza hadi kidato cha sita iliyopo Kalengakelu, Mlimba, Mkoa wa Morogoro. Shule inatoa mazingira bora ya kujifunzia, maabara za sayansi zenye vifaa vya kisasa, kompyuta course, na tahasusi zote kuu za Sayansi, Biashara na Sanaa.'
                  : 'Kaozya English Medium School ni shule ya mchepuo wa Kiingereza inayotoa malezi na elimu bora kuanzia Baby Class, Kindergarten, Upper Class, Pre-Unit hadi Darasa la Sita. Shule inatoa usafiri wa mabasi ya shule (School Bus), kifungua kinywa, chakula cha mchana na walimu waliobobea.'}
              </Typography>
              <Typography variant="body1" sx={{ fontSize: '1.05rem', lineHeight: 1.8, color: 'text.secondary', mb: 3 }}>
                Shule zote zinasimamiwa na <strong>Henry Laurent Kaozya</strong>, zikiwa kwenye eneo tulivu la <strong>Mtaa wa Usalama, Barabara ya Kaozya Road, Jengo Namba 54, Kalengakelu, Mlimba</strong>.
              </Typography>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 4 }}>
                {currentSchool.tags.map(tag => (
                  <Chip key={tag} label={tag}
                    icon={<CheckCircle sx={{ fontSize: '16px !important', color: `${currentSchool.color} !important` }} />}
                    sx={{ bgcolor: `${currentSchool.color}12`, color: currentSchool.color, fontWeight: 700,
                      border: `1px solid ${currentSchool.color}30` }} />
                ))}
              </Box>

              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 'auto' }}>
                <AccentButton endIcon={<ArrowForward />} onClick={() => navigate(schoolTab === 0 ? '/apply' : '/primary/apply')}>
                  Online Application
                </AccentButton>
                <Button
                  variant="contained"
                  href={currentSchool.downloadPdf}
                  download
                  startIcon={<Download />}
                  sx={{ bgcolor: currentSchool.color, color: 'white', fontWeight: 700, textTransform: 'none', borderRadius: 2 }}
                >
                  Download Fomu (PDF)
                </Button>
              </Box>
            </GlassCard>
          </Grid>

          {/* Official Poster / Photo */}
          <Grid item xs={12} md={5}>
            <GlassCard sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Box sx={{ position: 'relative', height: 260, bgcolor: '#111' }}>
                <CardMedia
                  component="img"
                  image={currentSchool.image}
                  alt={currentSchool.label}
                  sx={{ height: '100%', objectFit: 'cover' }}
                />
                <Chip
                  label="Official Campus Photo"
                  size="small"
                  sx={{ position: 'absolute', top: 12, left: 12, bgcolor: 'rgba(0,0,0,0.7)', color: 'white', fontWeight: 700 }}
                />
              </Box>
              <CardContent sx={{ p: 3, flexGrow: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: currentSchool.color, mb: 1.5 }}>
                  Takwimu na Sifa Kuu:
                </Typography>
                {currentSchool.stats.map(({ label, value, color }, i) => (
                  <Box key={label}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1 }}>
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>{label}</Typography>
                      <Typography variant="subtitle2" sx={{ color, fontWeight: 800 }}>{value}</Typography>
                    </Box>
                    {i < currentSchool.stats.length - 1 && <Divider sx={{ borderColor: 'rgba(0,0,0,0.06)' }} />}
                  </Box>
                ))}
              </CardContent>
            </GlassCard>
          </Grid>
        </Grid>
      </Container>

      {/* Official Bank Accounts Section */}
      <Box sx={{ bgcolor: C.white, py: 6, borderTop: `1px solid ${C.primary}10`, borderBottom: `1px solid ${C.primary}10` }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
            <AccountBalance sx={{ color: C.primary, fontSize: 32 }} />
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 800, color: C.primary }}>
                Akaunti Rasmi za Benki (Bank Payment Accounts)
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Malipo yote yafanyike benki kwenye akaunti zifuatazo:
              </Typography>
            </Box>
          </Box>

          <Grid container spacing={3}>
            {currentSchool.bankAccounts.map((b, i) => (
              <Grid item xs={12} sm={6} key={i}>
                <Paper sx={{ p: 3, borderRadius: 3, bgcolor: `${currentSchool.color}08`, border: `1px solid ${currentSchool.color}25` }}>
                  <Typography variant="caption" sx={{ color: currentSchool.color, fontWeight: 800, display: 'block' }}>
                    {b.bank}
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 900, color: currentSchool.color, my: 0.5 }}>
                    {b.acc}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Jina la Akaunti: <strong>{b.name}</strong>
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Contact Directory */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
        <SectionHeading subtitle="Wasiliana na Idara Mbalimbali za Shule">
          Mawasiliano na Idara (Contact Directory)
        </SectionHeading>

        <Grid container spacing={3} sx={{ mb: 4 }}>
          {currentSchool.contacts.map(({ role, numbers }, i) => (
            <Grid item xs={12} sm={6} md={4} key={role}>
              <GlassCard sx={{ height: '100%' }}>
                <Box sx={{ height: 4, background: `linear-gradient(90deg, ${currentSchool.color}, ${currentSchool.color}60)` }} />
                <CardContent sx={{ p: 2.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: currentSchool.color, mb: 1.5 }}>
                    {role}
                  </Typography>
                  {numbers.map(num => (
                    <Button key={num} fullWidth variant="outlined" size="small"
                      startIcon={<Phone />}
                      href={`tel:${num.replace(/\s/g, '')}`}
                      sx={{ mb: 0.75, borderRadius: 2, textTransform: 'none', fontWeight: 600, fontSize: 13,
                        borderColor: `${currentSchool.color}40`, color: currentSchool.color,
                        '&:hover': { bgcolor: `${currentSchool.color}08`, borderColor: currentSchool.color } }}>
                      {num}
                    </Button>
                  ))}
                </CardContent>
              </GlassCard>
            </Grid>
          ))}
        </Grid>

        {/* Owner Card */}
        <GlassCard>
          <Box sx={{ p: 3, background: `linear-gradient(135deg, ${C.accent}, #e65100)` }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ width: 56, height: 56, bgcolor: 'rgba(255,255,255,0.2)', fontWeight: 800, fontSize: 22 }}>
                HK
              </Avatar>
              <Box>
                <Typography variant="h6" sx={{ color: C.white, fontWeight: 800 }}>{OWNER.name}</Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>{OWNER.title}</Typography>
              </Box>
            </Box>
          </Box>
          <CardContent sx={{ p: 3 }}>
            <Grid container spacing={2}>
              {OWNER.numbers.map(num => (
                <Grid item xs={12} sm={6} md={3} key={num}>
                  <Button fullWidth variant="outlined" startIcon={<Phone />}
                    href={`tel:${num.replace(/\s/g, '')}`}
                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700,
                      borderColor: `${C.accent}50`, color: C.accent,
                      '&:hover': { bgcolor: `${C.accent}08`, borderColor: C.accent } }}>
                    {num}
                  </Button>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </GlassCard>
      </Container>

      {/* Location Map */}
      <Box sx={{ bgcolor: C.white, py: 8 }}>
        <Container maxWidth="lg">
          <SectionHeading subtitle="Kaozya Road, Jengo No. 54, Mtaa wa Usalama, Kalengakelu, Mlimba Morogoro">
            Mahali Shule Ilipo (Location)
          </SectionHeading>

          <Grid container spacing={4} alignItems="stretch">
            <Grid item xs={12} md={4}>
              <GlassCard sx={{ height: '100%', p: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 800, color: C.primary, mb: 2 }}>
                  Jinsi ya Kufika Shuleni:
                </Typography>
                <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
                  <DirectionsBus sx={{ color: C.primary }} />
                  <Typography variant="body2" color="text.secondary">
                    Ukifika stendi ya mabasi Mlimba, chukua usafiri wowote (Bodaboda au Bajaj - Nauli TSh 2,000/=). Ni kilometa 3 tu kutoka stendi mpaka shuleni.
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5, mb: 3 }}>
                  <LocationOn sx={{ color: C.accent }} />
                  <Typography variant="body2" color="text.secondary">
                    Mtaa wa Usalama, Barabara ya Kaozya Road, Jengo Namba 54.
                  </Typography>
                </Box>
                <Button
                  fullWidth
                  variant="contained"
                  href="https://maps.google.com/?q=Kaozya+Secondary+School"
                  target="_blank"
                  startIcon={<MapIcon />}
                  sx={{ bgcolor: C.primary, fontWeight: 700, borderRadius: 2, textTransform: 'none' }}
                >
                  Open in Google Maps
                </Button>
              </GlassCard>
            </Grid>

            <Grid item xs={12} md={8}>
              <GlassCard sx={{ height: 380, overflow: 'hidden' }}>
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d784786.7584530564!2d34.915760514927115!3d-8.857474534377747!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x18ff536ea135a4df%3A0x91919843dba9f9a2!2sKaozya%20Secondary%20School!5e0!3m2!1sen!2stz!4v1774947325835!5m2!1sen!2stz"
                  width="100%" height="100%" style={{ border: 0 }}
                  allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade"
                  title="Kaozya Schools Location"
                />
              </GlassCard>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Footer CTA */}
      <Box sx={{ py: 8, textAlign: 'center', background: `linear-gradient(135deg, ${C.dark} 0%, ${C.primary} 100%)`, color: 'white' }}>
        <Container maxWidth="md">
          <School sx={{ fontSize: 60, color: C.gold, mb: 1.5 }} />
          <Typography variant="h4" sx={{ fontWeight: 900, mb: 1 }}>
            Karibu Kaozya Schools 2026
          </Typography>
          <Typography variant="body1" sx={{ color: C.gold, mb: 3 }}>
            Jiandikishe sasa kwa elimu bora na mustakabali mwema wa mtoto wako.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <AccentButton endIcon={<ArrowForward />} onClick={() => navigate('/apply')}>
              Apply Online Now
            </AccentButton>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate('/fees')}
              sx={{ borderColor: C.gold, color: C.gold, fontWeight: 700, borderRadius: 2, textTransform: 'none' }}
            >
              Angalia Ada Kamili
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default AboutPage;
